import { useQuery } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { useMemo } from 'react';

import type { AuditReport } from '../domain';
import type { GraphQLCommunity } from '../domain/graphql-types';
import { GET_AUDIT_REPORTS_BY_DATE_RANGE } from '../graphql/queries/getAuditReportsByDateRange';

interface GetAuditReportsByDateRangeResponse {
  community: GraphQLCommunity & {
    auditReportsByDateRange: AuditReport[];
  };
}

interface UseGetAuditReportsByDateRangeParams {
  communityId: string | undefined;
  fromDate: string;
  toDate: string;
  options?: {
    skip?: boolean;
    pollInterval?: number;
  };
}

/**
 * Hook for fetching audit reports by date range for a specific community
 * Uses the new auditReportsByDateRange GraphQL field
 */
export function useGetAuditReportsByDateRange({ communityId, fromDate, toDate, options = {} }: UseGetAuditReportsByDateRangeParams) {
  const { skip = false, pollInterval } = options;

  const { data, loading, error, refetch } = useQuery<GetAuditReportsByDateRangeResponse>(GET_AUDIT_REPORTS_BY_DATE_RANGE, {
    variables: {
      communityId,
      fromDate,
      toDate,
    },
    skip: skip || !communityId || !fromDate || !toDate,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Instant cache + fresh network data
    nextFetchPolicy: 'cache-first', // Subsequent queries use cache-first
    notifyOnNetworkStatusChange: true, // Show loading during refetch
    pollInterval, // Optional polling for live updates
  });

  // Use GraphQL response directly - no unnecessary mapping layers
  const result = useMemo(() => {
    if (!data?.community) {
      return { auditReports: [], community: null };
    }

    // Use GraphQL data directly - no artificial transformations
    return {
      auditReports: data.community.auditReportsByDateRange || [],
      community: data.community,
    };
  }, [data]);

  // Transform error using simplified adapter pattern
  const domainError = useMemo(() => {
    if (!error) return undefined;

    // Map GraphQL error to user-friendly format
    const mappedError = transformError(error);

    // Return error with onRetry function for compatibility with existing UI
    return {
      ...mappedError,
      onRetry: () => refetch(),
    };
  }, [error, refetch]);

  return {
    auditReports: result.auditReports,
    community: result.community,
    loading,
    error: domainError,
    refetch,
  };
}
